// Marketplace + equipment constants (kept in sync with mobile)

export const CATEGORIES = [
  { id: "wheel_base", label: "Wheel Bases" },
  { id: "steering_wheel", label: "Steering Wheels" },
  { id: "pedals", label: "Pedals" },
  { id: "cockpit", label: "Cockpits & Rigs" },
  { id: "shifter", label: "Shifters & Handbrakes" },
  { id: "button_box", label: "Button Boxes" },
  { id: "motion", label: "Motion Systems" },
  { id: "display", label: "Displays & Monitors" },
  { id: "vr", label: "VR Headsets" },
  { id: "seat", label: "Seats" },
  { id: "accessories", label: "Accessories" },
  { id: "pc_hardware", label: "PC Hardware" },
  { id: "complete_rig", label: "Complete Rigs" },
  { id: "other", label: "Other" },
];

export const CONDITIONS = [
  { id: "new", label: "New (Sealed)", color: "#00E676", description: "Brand new, unopened, with original packaging" },
  { id: "like_new", label: "Like New", color: "#69F0AE", description: "Used 1-2 times, no visible wear, with all accessories" },
  { id: "excellent", label: "Excellent", color: "#00BCD4", description: "Light use, minimal signs of wear, fully functional" },
  { id: "good", label: "Good", color: "#FFD700", description: "Regular use, some cosmetic wear, fully functional" },
  { id: "fair", label: "Fair", color: "#FF9100", description: "Heavy use, visible wear, but still functional" },
  { id: "parts_only", label: "For Parts/Repair", color: "#FF5252", description: "Not fully functional, sold as-is for parts" },
];

export const SHIPPING_OPTIONS = [
  { id: "ship_only", label: "Shipping Only" },
  { id: "local_only", label: "Local Pickup Only" },
  { id: "both", label: "Ship or Local Pickup" },
];

export const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£", CAD: "C$", AUD: "A$" };

export const STANDARD_BOX_SIZES = {
  XS: { label: "Extra Small", dimensions: "20 x 15 x 10 cm" },
  S: { label: "Small", dimensions: "30 x 25 x 15 cm" },
  M: { label: "Medium", dimensions: "40 x 30 x 20 cm" },
  L: { label: "Large", dimensions: "50 x 40 x 30 cm" },
  XL: { label: "Extra Large", dimensions: "60 x 50 x 40 cm" },
  XXL: { label: "XX-Large", dimensions: "80 x 60 x 50 cm" },
  OVERSIZED: { label: "Oversized", dimensions: "100 x 80 x 60 cm" },
  COCKPIT_SM: { label: "Cockpit Small", dimensions: "120 x 60 x 40 cm" },
  COCKPIT_MD: { label: "Cockpit Medium", dimensions: "150 x 80 x 50 cm" },
  COCKPIT_LG: { label: "Cockpit Large", dimensions: "180 x 100 x 60 cm" },
  CUSTOM: { label: "Custom Size", dimensions: "Enter dimensions" },
};

// Brands per category — mirrors mobile marketplace constants
export const BRANDS = {
  wheel_base: ["Fanatec", "Logitech", "Simucube", "MOZA Racing", "Simagic", "Asetek SimSports", "Thrustmaster", "VRS", "Cammus", "Conspit", "AccuForce", "SimXperience", "Other"],
  steering_wheel: ["Fanatec", "Cube Controls", "Ascher Racing", "MOZA Racing", "Simagic", "Precision Sim Engineering", "Turn Racing", "Gomez Sim Industries", "Bavarian Sim Tech", "Thrustmaster", "Logitech", "Simline", "Other"],
  pedals: ["Heusinkveld", "Fanatec", "Simucube", "MOZA Racing", "Simagic", "Asetek SimSports", "Logitech", "Thrustmaster", "Simforge", "Sprints", "VRS", "Simtag", "Wave Italy", "Other"],
  cockpit: ["Sim-Lab", "Next Level Racing", "Trak Racer", "Playseat", "GT Omega", "Fanatec", "RCP", "SimCraft", "Advanced Sim Racing", "Pit Lane Sim Racing", "RSeat", "Motedis", "8020 Aluminum (Custom)", "Other"],
  shifter: ["Fanatec", "Heusinkveld", "Thrustmaster", "Logitech", "Simagic", "MOZA Racing", "Aiologs", "Pro-Sim", "VNE Simulation", "Other"],
  button_box: ["Apex Sim Racing", "RaceBox", "Grid Engineering", "BoxOneRacing", "ignitiON Controls", "BBJ SimRacing", "SRC", "Derek Speare Designs", "Elgato Stream Deck", "Fanatec", "Thrustmaster", "DIY / Custom", "Other"],
  motion: ["DOF Reality", "Next Level Racing", "SimXperience", "PT Actuator", "SimCraft", "Qubic System", "SFX-100", "Motion Systems", "Other"],
  display: ["Samsung", "LG", "Dell", "ASUS", "AOC", "MSI", "ViewSonic", "BenQ", "Acer", "Simucube (Dashboards)", "VoCore", "Other"],
  vr: ["Meta (Quest)", "HP Reverb", "Valve Index", "HTC Vive", "Pimax", "Varjo", "Samsung Odyssey", "Bigscreen Beyond", "Other"],
  seat: ["Sparco", "Bride", "OMP", "Recaro", "Corbeau", "Sim-Lab", "Next Level Racing", "Trak Racer", "Playseat", "NRG", "Other"],
  accessories: ["Buttkicker", "SimHub", "TrackIR", "Tobii Eye Tracker", "Elgato", "Fanatec", "Sim-Lab", "JetSeat", "SimVibe", "Other"],
  pc_hardware: ["NVIDIA", "AMD", "Intel", "Corsair", "ASUS", "MSI", "Gigabyte", "Other"],
  complete_rig: ["Sim-Lab", "Next Level Racing", "Trak Racer", "Advanced Sim Racing", "Pit Lane Sim Racing", "Custom Build", "Other"],
  other: ["Other"],
};

export const MESSAGE_TEMPLATES = [
  "Is this still available?",
  "What is your lowest price?",
  "Can you describe the condition in more detail?",
  "How much would shipping be to my location?",
  "Would you be willing to meet locally?",
  "Would you consider a bundle deal?",
];

export const EQUIPMENT_FIELDS = [
  { key: "wheel_base", label: "Wheel Base" },
  { key: "steering_wheel", label: "Steering Wheel" },
  { key: "pedals", label: "Pedals" },
  { key: "shifter", label: "Shifter" },
  { key: "handbrake", label: "Handbrake" },
  { key: "cockpit", label: "Cockpit / Stand" },
  { key: "motion_system", label: "Motion System" },
];

export const categoryLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;
export const conditionMeta = (id) => CONDITIONS.find((c) => c.id === id) || { label: id, color: "#A0A0B0" };
export const brandsForCategory = (id) => BRANDS[id] || BRANDS.other;
export const money = (amount, currency = "USD") =>
  `${CURRENCY_SYMBOLS[currency] || "$"}${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Services
export const servicePriceLabel = (listing) => {
  if (listing?.service_type === "request") {
    return Number(listing.price) > 0 ? `Budget ${money(listing.price, listing.currency)}` : "Open budget";
  }
  if (listing?.pricing_type === "quote") return "Contact for quote";
  if (listing?.pricing_type === "hourly") return `${money(listing.price, listing.currency)}/hr`;
  return money(listing?.price, listing?.currency);
};
